<script setup lang="ts">
import BaseIcon from '@/components/base/BaseIcon.vue'

/**
 * The shell for every credential screen: sign in, forgot, reset, change.
 *
 * A 5:7 split. The brand panel is the **rail surface** — theme-invariant, the same dark
 * ground the signed-in shell is anchored by — so signing in is visually continuous with the
 * panel rather than a jump between two products. Three short rows state the three things
 * an operator is accountable for; nothing else, because a sign-in screen is read for about
 * four seconds.
 *
 * It carries no navigation and nothing permission-dependent: it renders for visitors with
 * no session, and anything here leaks what the panel contains. The copy is about *conduct*,
 * not features.
 *
 * Below `lg` the panel is dropped rather than stacked — on a phone it would push the form
 * below the fold, and this is the one screen where the form is the entire purpose.
 */
const ASSURANCES = [
  {
    icon: 'clipboard-text',
    title: 'Every action is recorded',
    body: 'Attributed to you, in the audit log, permanently.',
  },
  {
    icon: 'shield-check',
    title: 'Access is per permission',
    body: 'Roles grant what you can see. Nothing is implied by seniority.',
  },
  {
    icon: 'warning',
    title: 'High-risk actions are marked',
    body: 'Anything that reaches a live storefront says so before you act.',
  },
] as const
</script>

<template>
  <div class="grid min-h-screen bg-canvas lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
    <aside class="hidden flex-col bg-rail px-12 py-10 lg:flex">
      <div class="flex items-center gap-2.5">
        <span class="grid size-8 place-items-center rounded-md bg-rail-active text-rail-accent">
          <BaseIcon
            name="lock-key"
            :size="18"
            weight="fill"
          />
        </span>
        <span class="text-title-sm text-rail-ink-strong">Zapio</span>
        <span class="text-micro text-rail-label">Operator</span>
      </div>

      <div class="my-auto max-w-md py-12">
        <p class="text-micro text-rail-accent">Support &amp; administration</p>
        <h1 class="mt-3 text-display text-rail-ink-strong">
          One console for every merchant question.
        </h1>
        <p class="mt-4 text-body text-rail-ink">
          Shops, promotions, discounts and the people who manage them — answered in one
          sitting.
        </p>

        <ul class="mt-10 divide-y divide-rail-active border-y border-rail-active">
          <li
            v-for="row in ASSURANCES"
            :key="row.title"
            class="flex items-start gap-3.5 py-3.5"
          >
            <span class="mt-0.5 text-rail-accent">
              <BaseIcon
                :name="row.icon"
                :size="18"
              />
            </span>
            <span>
              <span class="block text-body font-medium text-rail-ink-strong">{{ row.title }}</span>
              <span class="block text-caption text-rail-label">{{ row.body }}</span>
            </span>
          </li>
        </ul>
      </div>

      <p class="text-caption text-rail-label">Authorised staff only · Sessions expire after 12 hours.</p>
    </aside>

    <main class="flex items-center justify-center px-6 py-12">
      <div class="w-full max-w-[22.5rem]">
        <!-- The brand repeats here for the small-screen case, where the panel is gone. -->
        <div class="mb-8 flex items-center gap-2 lg:hidden">
          <span class="text-title-sm text-ink">Zapio</span>
          <span class="text-micro text-ink-muted">Operator</span>
        </div>
        <slot />
      </div>
    </main>
  </div>
</template>
